'use client'

import Link from 'next/link'
import Image from 'next/image'
import { Play } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

import { containerClassName } from '@/components/Container'
import { ImageGalleryStripCarousel } from '@/components/Gallery/ImageGalleryStripCarousel'
import type { ImageGalleryStripSlide } from '@/components/Gallery/imageGalleryStrip'
import { VideoModal } from '@/components/VideoModal'
import { Button } from '@/components/ui/button'
import { useMessages } from '@/lib/i18n/useMessages'
import { useLocale } from '@/lib/i18n/useLocale'
import type { ResolvedPopupVideo } from '@/lib/media/resolvePopupVideo'
import { getBookingHref } from '@/lib/pages/bookingPaths'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import { cn } from '@/utilities/ui'

const CITY_AERIAL_IMAGE = '/media/homepage/video-gallery/city-aerial.png'
const EAGLE_MASCOT_IMAGE = '/media/homepage/video-gallery/eagle-mascot.png'

const STRIP_IMAGES = [
  '/media/homepage/video-gallery/strip-1.png',
  '/media/homepage/video-gallery/strip-2.png',
  '/media/homepage/video-gallery/strip-3.png',
  '/media/homepage/video-gallery/strip-4.png',
] as const

type VideoGallerySectionProps = {
  className?: string
  pullUp?: boolean
  slides?: ImageGalleryStripSlide[]
  video?: ResolvedPopupVideo
}

export const VideoGallerySection: React.FC<VideoGallerySectionProps> = ({
  className,
  pullUp = true,
  slides,
  video,
}) => {
  const messages = useMessages()
  const locale = useLocale()
  const copy = messages.landing.videoGallery
  const [videoOpen, setVideoOpen] = useState(false)
  const playButtonRef = useRef<HTMLButtonElement>(null)
  const hasVideo = Boolean(video?.src)

  const stripSlides: ImageGalleryStripSlide[] =
    slides && slides.length > 0
      ? slides
      : STRIP_IMAGES.map((image, index) => ({
          id: `strip-${index + 1}`,
          image,
          imageAlt: copy.stripAlts[index] ?? '',
        }))

  const handleCloseVideo = useCallback(() => {
    setVideoOpen(false)
    playButtonRef.current?.focus()
  }, [])

  return (
    <section
      aria-labelledby="video-gallery-heading"
      className={cn('isolate overflow-x-clip', className)}
    >
      <div
        className={cn(
          'relative min-h-[28rem] overflow-hidden lg:min-h-[45rem]',
          pullUp ? 'pb-0' : 'pb-10 lg:pb-16',
        )}
      >
        <Image
          alt=""
          aria-hidden
          className="object-cover object-center"
          fill
          sizes="100vw"
          src={CITY_AERIAL_IMAGE}
        />
        <div aria-hidden className="absolute inset-0 bg-bluetext/75" />

        <Image
          alt=""
          aria-hidden
          className="pointer-events-none absolute bottom-24 right-4 z-30 hidden h-auto w-36 lg:right-8 lg:block lg:w-52 xl:w-86"
          height={616}
          src={EAGLE_MASCOT_IMAGE}
          width={720}
        />

        <div className={cn(containerClassName, 'relative z-10 py-12 lg:py-16')}>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] lg:items-center lg:gap-8 pb-16 lg:pb-0">
            <div className="max-w-xl lg:pb-20">
              <h2
                className="text-balance font-display text-[2.5rem] font-semibold leading-[1.05] text-white md:text-[3.5rem] lg:text-[4rem]"
                id="video-gallery-heading"
              >
                <span className="block">{copy.titleLine1}</span>
                <span className="block">
                  {copy.titleLine2}{' '}
                  <span className="text-turquoise">{copy.titleHighlight}</span>
                </span>
                <span className="block">{copy.titleLine3}</span>
              </h2>

              <p className="mt-6 max-w-md font-outfit text-base leading-relaxed text-white/90">
                {copy.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  asChild
                  className="border-turquoise bg-transparent text-turquoise hover:bg-turquoise/10"
                  variant="outline"
                >
                  <Link href={getBookingHref(locale)}>{copy.startBooking}</Link>
                </Button>
                <Button
                  asChild
                  className="border-turquoise bg-transparent text-turquoise hover:bg-turquoise/10"
                  variant="outline"
                >
                  <Link href={getInspectionCalendarHref()} rel="noopener noreferrer" target="_blank">
                    {copy.bookVisit}
                    <span className="sr-only"> {messages.common.opensInNewTab}</span>
                  </Link>
                </Button>
              </div>

              <div className="mt-10 font-outfit text-sm leading-relaxed text-white/90">
                <p className="font-semibold text-white">{copy.helpTitle}</p>
                <p>{copy.helpBody}</p>
                <p className="mt-2">
                  <a
                    className="inline-flex min-h-6 items-center underline-offset-2 hover:underline"
                    href={`tel:${copy.phoneHref}`}
                  >
                    {copy.phone}
                  </a>
                </p>
                <p>
                  <a
                    className="inline-flex min-h-6 items-center underline-offset-2 hover:underline"
                    href={`mailto:${copy.email}`}
                  >
                    {copy.email}
                  </a>
                </p>
              </div>
            </div>

            {hasVideo ? (
              <div className="flex flex-col items-center justify-center lg:items-center">
                <button
                  ref={playButtonRef}
                  aria-label={copy.playAriaLabel}
                  className="inline-flex flex-col items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  onClick={() => setVideoOpen(true)}
                  type="button"
                >
                  <span className="inline-flex size-20 items-center justify-center rounded-full bg-turquoise shadow-lg md:size-24">
                    <Play
                      aria-hidden
                      className="ml-1 size-8 fill-bluetext text-bluetext md:size-10"
                    />
                  </span>
                  <span className="font-outfit text-base font-medium text-white">{copy.watchHow}</span>
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <ImageGalleryStripCarousel
        ariaLabel={copy.imageStripAriaLabel}
        carouselNextLabel={copy.carouselNext}
        carouselPreviousLabel={copy.carouselPrevious}
        pullUp={pullUp}
        slides={stripSlides}
      />

      {hasVideo && video ? (
        <VideoModal
          closeLabel={copy.closeVideoLabel}
          onClose={handleCloseVideo}
          open={videoOpen}
          title={copy.modalTitle}
          videoMimeType={video.mimeType}
          videoUrl={video.src}
        />
      ) : null}
    </section>
  )
}
